import { useEffect, useState } from 'react'
import { MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ds2'
import { expireAssigned, undoAssign, useAssignStore, type AssignTarget } from './assignStore'

interface AssignDriverActionProps {
    tabId: string
    loadId: string
    /** The load has a truck now (the row shows it): its action is Reassign behind "⋯", not Assign driver. */
    hasTruck: boolean
    /** The truck the row shows: Unassign takes it off, also after a reload (when the stored write is all the chat knows). */
    target?: AssignTarget
    /** The picker is open: the row's action hides, the picker sits under the row. */
    picking: boolean
    onOpen: () => void
}

/**
 * The row's assign action, in the action column. A load with no truck offers "Assign driver"; a load with one offers Reassign behind
 * "⋯". Right after an assign the row offers Undo until its window closes.
 */
export const AssignDriverAction = ({ tabId, loadId, hasTruck, target, picking, onOpen }: AssignDriverActionProps) => {
    const assigned = useAssignStore((state) => state.assigned[loadId])
    const [menu, setMenu] = useState(false)
    const undoUntil = assigned?.undoUntil

    // The Undo window closes on its own: the row is read again once it has passed.
    useEffect(() => {
        if (undoUntil === undefined) return undefined
        const timer = setTimeout(() => expireAssigned(loadId), Math.max(0, undoUntil - Date.now()))
        return () => clearTimeout(timer)
    }, [undoUntil, loadId])

    if (assigned && Date.now() < assigned.undoUntil)
        return (
            <Button size="sm" variant="outline" onClick={() => void undoAssign(tabId, loadId)}>
                Undo
            </Button>
        )
    if (picking) return null
    if (!hasTruck)
        return (
            <Button size="sm" variant="secondary" onClick={onOpen}>
                Assign driver
            </Button>
        )
    return (
        <div className="relative flex items-center justify-end">
            <Button variant="ghost" size="sm" iconOnly aria-label="More actions" aria-expanded={menu} onClick={() => setMenu(!menu)}>
                <MoreHorizontal className="h-4 w-4" aria-hidden />
            </Button>
            {menu && (
                <div className="absolute right-0 top-full z-20 mt-1 flex w-40 flex-col rounded-xl bg-surface p-1 shadow-overlay">
                    <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() => {
                            setMenu(false)
                            onOpen()
                        }}
                    >
                        Reassign
                    </Button>
                    {target && (
                        <Button
                            variant="tertiary"
                            size="sm"
                            onClick={() => {
                                setMenu(false)
                                void undoAssign(tabId, loadId, target)
                            }}
                        >
                            Unassign
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}
